import "./POApprovals.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../../../Services/authStore";
import {
  toastSuccess,
  toastError,
  toastWarning,
} from "../../../Services/toaster";

export default function POApprovals() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [approvalAction, setApprovalAction] = useState("");
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [remarksError, setRemarksError] = useState("");

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchMyApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/po/approvals/my-approvals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      const data = response.data?.data || [];

      // Debug logging
      console.log("User from auth:", user);
      console.log("User ID from auth:", user?.user_id);
      console.log("Raw approvals data:", data);

      const mappedApprovals = data.map((item) => {
        // The API returns approval_status, not status
        // The API returns can_take_action directly
        return {
          id: item.id,
          po_id: item.po_id,
          po_number: item.po_number,
          vendor_name: item.vendor_name,
          vendor_code: item.vendor_code,
          doc_total: item.doc_total,
          po_date: item.po_date,
          required_date: item.required_date,
          department: item.department,
          status: item.approval_status, // Use approval_status from API
          can_take_action: item.can_take_action === true,
          approval_level: `Level ${item.approval_level}`, // Format as string
          remarks: item.remarks,
          approver_remarks: item.approval_remarks, // Use approval_remarks from API
          lines: item.lines || [],
          po_status_field: item.po_status_field,
          po_approval_status: item.po_approval_status,
          created_at: item.created_at,
          updated_at: item.updated_at,
          // Add any other fields you need
        };
      });

      console.log("Mapped approvals:", mappedApprovals);
      setApprovals(mappedApprovals);
    } catch (err) {
      console.error("Failed to load approvals:", err.response?.data);
      toastError("Failed to load approvals");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, user]);

  useEffect(() => {
    fetchMyApprovals();
  }, [fetchMyApprovals]);

  const updateApproval = async (approvalData) => {
    try {
      const userId = user?.user_id || user?.id;
      console.log("Updating approval with user_id:", userId);
      console.log("Approval data:", approvalData);

      const payload = {
        id: approvalData.id,
        po_id: approvalData.po_id,
        status: approvalData.status,
        remarks: approvalData.remarks,
        user_id: userId,
      };

      console.log("Update payload:", payload);

      const response = await axios.put(
        `${API_URL}/api/po/approvals/update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update approval error:", err.response?.data);
      throw err;
    }
  };

  const handleApprove = (approval) => {
    setSelectedApproval(approval);
    setApprovalAction("Approved");
    setApprovalRemarks("");
    setRemarksError("");
    setShowApprovalModal(true);
  };

  const handleReject = (approval) => {
    setSelectedApproval(approval);
    setApprovalAction("Rejected");
    setApprovalRemarks("");
    setRemarksError("");
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!selectedApproval) return;

    if (approvalAction === "Rejected" && !approvalRemarks.trim()) {
      setRemarksError("Remarks are required when rejecting a PO");
      toastWarning("Please provide remarks for rejection");
      return;
    }

    setRemarksError("");
    setSubmitting(true);

    try {
      await updateApproval({
        id: selectedApproval.id,
        po_id: selectedApproval.po_id,
        status: approvalAction,
        remarks:
          approvalRemarks ||
          `${approvalAction} on ${new Date().toLocaleDateString()}`,
      });

      toastSuccess(`PO ${approvalAction} successfully`);
      setApprovals((prev) =>
        prev.map((a) =>
          a.id === selectedApproval.id
            ? { ...a, status: approvalAction, remarks: approvalRemarks }
            : a,
        ),
      );

      setShowApprovalModal(false);
      setSelectedApproval(null);
      setApprovalRemarks("");
      setApprovalAction("");
      setRemarksError("");
      fetchMyApprovals();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "Failed to update approval";
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowApprovalModal(false);
    setSelectedApproval(null);
    setApprovalRemarks("");
    setApprovalAction("");
    setRemarksError("");
  };

  const filteredApprovals = approvals.filter((approval) => {
    const matchStatus =
      statusFilter === "All" || approval.status === statusFilter;
    const matchSearch =
      (approval.po_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (approval.vendor_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (approval.approval_level || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  const pendingCount = approvals.filter((a) => a.status === "Pending").length;
  const approvedCount = approvals.filter((a) => a.status === "Approved").length;
  const rejectedCount = approvals.filter((a) => a.status === "Rejected").length;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusClass = (status) => {
    const map = {
      Pending: "po-approval-status-pending",
      Approved: "po-approval-status-approved",
      Rejected: "po-approval-status-rejected",
    };
    return map[status] || "po-approval-status-pending";
  };

  const getLevelClass = (level) => {
    const map = {
      "Level 1": "level-1",
      "Level 2": "level-2",
      "Level 3": "level-3",
    };
    return map[level] || "level-1";
  };

  return (
    <div className="po-approval-page">
      <div className="po-approval-header">
        <div className="po-approval-header-left">
          <h1 className="po-approval-title">PO Approvals</h1>
          <p className="po-approval-subtitle">
            Review and manage purchase order approvals
          </p>
        </div>

        <div className="po-approval-header-right">
          <div className="po-approval-search">
            <span className="po-approval-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search approvals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="po-approval-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>

          <div className="po-approval-summary">
            <div className="po-approval-summary-pill po-approval-sum-pending">
              <span className="po-approval-sum-num">{pendingCount}</span>
              <span className="po-approval-sum-lbl">Pending</span>
            </div>
            <div className="po-approval-summary-pill po-approval-sum-approved">
              <span className="po-approval-sum-num">{approvedCount}</span>
              <span className="po-approval-sum-lbl">Approved</span>
            </div>
            <div className="po-approval-summary-pill po-approval-sum-rejected">
              <span className="po-approval-sum-num">{rejectedCount}</span>
              <span className="po-approval-sum-lbl">Rejected</span>
            </div>
          </div>
        </div>
      </div>

      <div className="po-approval-list">
        {loading ? (
          <div className="po-approval-empty">
            <p>Loading approvals...</p>
          </div>
        ) : filteredApprovals.length === 0 ? (
          <div className="po-approval-empty">
            <div className="po-approval-empty-icon">✅</div>
            <div className="po-approval-empty-title">All Clear!</div>
            <div className="po-approval-empty-desc">
              {searchTerm
                ? `No approvals match "${searchTerm}"`
                : "No pending approvals at this time."}
            </div>
          </div>
        ) : (
          filteredApprovals.map((approval) => {
            const isPending = approval.status === "Pending";
            const canAct = approval.can_take_action === true;

            return (
              <div key={approval.id} className="po-approval-card">
                <div className="po-approval-level">
                  <span
                    className={`po-approval-level-badge ${getLevelClass(approval.approval_level)}`}
                  >
                    {approval.approval_level}
                  </span>
                </div>

                <div className="po-approval-content">
                  <div className="po-approval-header-row">
                    <div>
                      <div className="po-approval-po-number">
                        PO: {approval.po_number}
                      </div>
                      <div className="po-approval-vendor">
                        {approval.vendor_name}
                      </div>
                    </div>
                    <span
                      className={`po-approval-status-badge ${getStatusClass(approval.status)}`}
                    >
                      {approval.status}
                    </span>
                  </div>

                  <div className="po-approval-details">
                    <div className="po-approval-detail-item">
                      <span className="po-approval-detail-label">Amount</span>
                      <span className="po-approval-detail-value">
                        {formatCurrency(approval.doc_total)}
                      </span>
                    </div>
                    <div className="po-approval-detail-item">
                      <span className="po-approval-detail-label">Date</span>
                      <span className="po-approval-detail-value">
                        {formatDate(approval.po_date)}
                      </span>
                    </div>
                    <div className="po-approval-detail-item">
                      <span className="po-approval-detail-label">
                        Required By
                      </span>
                      <span className="po-approval-detail-value">
                        {formatDate(approval.required_date)}
                      </span>
                    </div>
                    <div className="po-approval-detail-item">
                      <span className="po-approval-detail-label">
                        Department
                      </span>
                      <span className="po-approval-detail-value">
                        {approval.department}
                      </span>
                    </div>
                    <div className="po-approval-detail-item po-approval-detail-full">
                      <span className="po-approval-detail-label">Remarks</span>
                      <span className="po-approval-detail-value">
                        {approval.remarks || "No remarks"}
                      </span>
                    </div>
                    {approval.approver_remarks && (
                      <div className="po-approval-detail-item po-approval-detail-full">
                        <span className="po-approval-detail-label">
                          Approver Remarks
                        </span>
                        <span className="po-approval-detail-value po-approval-remarks">
                          {approval.approver_remarks}
                        </span>
                      </div>
                    )}
                  </div>

                  {approval.lines && approval.lines.length > 0 && (
                    <div className="po-approval-lines">
                      <div className="po-approval-lines-header">
                        <span>Line Items</span>
                        <span>{approval.lines.length} items</span>
                      </div>
                      <div className="po-approval-lines-list">
                        {approval.lines.slice(0, 3).map((line, idx) => (
                          <div key={idx} className="po-approval-line-item">
                            <span>{line.description}</span>
                            <span>
                              {line.qty} x {formatCurrency(line.price)}
                            </span>
                          </div>
                        ))}
                        {approval.lines.length > 3 && (
                          <div className="po-approval-line-more">
                            +{approval.lines.length - 3} more items
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {isPending && canAct ? (
                    <div className="po-approval-actions">
                      <button
                        className="po-approval-btn po-approval-btn-approve"
                        onClick={() => handleApprove(approval)}
                        disabled={submitting}
                      >
                        ✓ Approve
                      </button>
                      <button
                        className="po-approval-btn po-approval-btn-reject"
                        onClick={() => handleReject(approval)}
                        disabled={submitting}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  ) : isPending && !canAct ? (
                    <div className="po-approval-actions">
                      <div className="po-approval-waiting">
                        ⏳ Waiting for previous level approval
                      </div>
                    </div>
                  ) : (
                    <div className="po-approval-actions">
                      <div
                        className={`po-approval-actioned ${approval.status === "Approved" ? "po-approval-actioned-ok" : "po-approval-actioned-no"}`}
                      >
                        {approval.status === "Approved"
                          ? "✓ Approved"
                          : "✗ Rejected"}
                      </div>
                      {approval.approver_remarks && (
                        <div className="po-approval-actioned-remarks">
                          "{approval.approver_remarks}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showApprovalModal && selectedApproval && (
        <div className="po-approval-modal-overlay" onClick={handleModalClose}>
          <div
            className="po-approval-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="po-approval-modal-title">
              {approvalAction === "Approved" ? "Approve" : "Reject"} PO
            </h2>
            <p className="po-approval-modal-sub">
              {approvalAction === "Approved"
                ? "Confirm approval for this purchase order"
                : "Please provide a reason for rejecting this purchase order"}
            </p>

            <div className="po-approval-modal-details">
              <div className="po-approval-modal-detail">
                <span>PO Number</span>
                <strong>{selectedApproval.po_number}</strong>
              </div>
              <div className="po-approval-modal-detail">
                <span>Vendor</span>
                <strong>{selectedApproval.vendor_name}</strong>
              </div>
              <div className="po-approval-modal-detail">
                <span>Total Amount</span>
                <strong>{formatCurrency(selectedApproval.doc_total)}</strong>
              </div>
              <div className="po-approval-modal-detail">
                <span>Approval Level</span>
                <strong>{selectedApproval.approval_level}</strong>
              </div>
            </div>

            <div className="po-approval-modal-remarks">
              <label>
                Remarks{" "}
                {approvalAction === "Rejected" && (
                  <span className="po-approval-required">*</span>
                )}
                {approvalAction === "Rejected" && (
                  <span className="po-approval-required-text">
                    {" "}
                    (Required for rejection)
                  </span>
                )}
              </label>
              <textarea
                rows="3"
                value={approvalRemarks}
                onChange={(e) => {
                  setApprovalRemarks(e.target.value);
                  if (remarksError) setRemarksError("");
                }}
                placeholder={
                  approvalAction === "Rejected"
                    ? "Please provide a reason for rejection..."
                    : "Add remarks (optional)..."
                }
                className={remarksError ? "po-approval-textarea-error" : ""}
              />
              {remarksError && (
                <div className="po-approval-error-message">{remarksError}</div>
              )}
            </div>

            <div className="po-approval-modal-actions">
              <button
                className="po-approval-btn po-approval-btn-secondary"
                onClick={handleModalClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={`po-approval-btn ${approvalAction === "Approved" ? "po-approval-btn-approve" : "po-approval-btn-reject"}`}
                onClick={handleApprovalSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Processing..."
                  : approvalAction === "Approved"
                    ? "✓ Approve"
                    : "✗ Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
